
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Tribute {
  id: string;
  memorial_id: string;
  author: string;
  content: string;
  photo_url?: string;
  created_at: string;
}

export const useTributes = (memorialId: string) => {
  const [tributes, setTributes] = useState<Tribute[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTributes = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('tributes')
        .select('*')
        .eq('memorial_id', memorialId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTributes(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching tributes:', err);
    } finally {
      setIsLoading(false);
    }
  }, [memorialId]);

  useEffect(() => {
    fetchTributes();

    // Set up real-time subscription
    const channel = supabase
      .channel('tributes-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tributes',
          filter: `memorial_id=eq.${memorialId}`
        },
        (payload) => {
          const newTribute = payload.new as Tribute;
          if (newTribute && newTribute.memorial_id === memorialId) {
            setTributes(currentTributes => {
              const updatedTributes = [newTribute, ...currentTributes];
              return updatedTributes;
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'tributes'
          // Remove the filter for DELETE events to catch all deletions
        },
        (payload) => {
          if (payload.old && payload.old.id) {
            const deletedId = payload.old.id;
            setTributes(prevTributes => {
              const updatedTributes = prevTributes.filter(tribute => tribute.id !== deletedId);
              return updatedTributes;
            });
          } else {
            console.error('Deleted payload missing ID:', payload);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [memorialId, fetchTributes]);

  const addTribute = async (author: string, content: string, photo?: File | null) => {
    try {
      let photoUrl = null;
      
      // Upload photo if provided
      if (photo) {
        const fileExt = photo.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `tributes/${memorialId}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('story-media')
          .upload(filePath, photo);
          
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('story-media')
          .getPublicUrl(filePath);
          
        photoUrl = publicUrl;
      }
      
      const { error, data } = await supabase
        .from('tributes')
        .insert({
          memorial_id: memorialId,
          author,
          content,
          photo_url: photoUrl
        })
        .select();

      if (error) throw error;

      // Manually update the UI for redundancy, in case the real-time subscription fails
      if (data && data.length > 0) {
        setTributes(prevTributes => [data[0], ...prevTributes]);
      }
      
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error adding tribute:', err);
      throw err;
    }
  };

  const deleteTribute = async (tributeId: string) => {
    try {
      const { error } = await supabase
        .from('tributes')
        .delete()
        .eq('id', tributeId);

      if (error) throw error;

      // Manually update the UI for redundancy
      setTributes(prevTributes => {
        return prevTributes.filter(tribute => tribute.id !== tributeId);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error deleting tribute:', err);
      throw err;
    }
  };

  return {
    tributes,
    isLoading,
    error,
    addTribute,
    deleteTribute
  };
};
